'use client';
import { useMediaQuery, useTimeout } from '@intigral/hooks';
import { FontIcon, HeroBlock, LazyImage, MovieDetails, Skeleton, ImageContainer, Price } from '@intigral/shared';
import { PriceProps } from '@intigral/shared/lib/components/Price';
import { isValidProviderProp } from '@intigral/shared/components/MovieDetails';
import cn from 'classnames';
import React, { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MovieButtons } from '../MovieButtons';
import styles from './movie.module.sass';

interface BaseProps
    extends React.HTMLAttributes<HTMLDivElement>,
        React.ComponentProps<typeof MovieButtons>,
        React.ComponentProps<typeof MovieDetails>,
        React.ComponentProps<typeof HeroBlock> {
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    isLoading: true;
    cover?: string;
    price?: string | PriceProps;
    timer?: React.ReactElement;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    icon?: 'remind' | 'locked';
    headingImage?: string;
    guid?: string;
    layout?: 'horizontal' | 'vertical' | 'vertical-2x' | 'default';
    rented?: boolean;
}

interface LoadedProps extends BaseProps {
    isLoading?: false;
    cover: string;
    price?: string | PriceProps;
    timer?: React.ReactElement;
    setIsActive?: React.Dispatch<React.SetStateAction<boolean>>;
    icon?: 'remind' | 'locked';
    guid?: string;
    layout?: 'horizontal' | 'vertical' | 'vertical-2x' | 'default';
    headingImage?: string;
    rented?: boolean;
}

type MovieCardProps = LoadingProps | LoadedProps;

export const MovieCard = forwardRef(
    (
        {
            cover,
            price,
            timer,
            setIsActive,
            icon,
            isLoading,
            layout = 'default',
            headingImage,
            rented = false,
            ...props
        }: MovieCardProps,
        ref
    ) => {
        const {
            play,
            progress,
            watchlist,
            watchlistButton,
            page,
            trailer,
            download,
            reminderButton,
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
            picture,
            heading,
            compact,
            minHeight,
            detail,
            theme,
            blurred,
            children,
            className,
            onClick,
            guid,
            ...restDivProps
        } = props;

        const movieButtonsProps = {
            play,
            progress,
            watchlist,
            watchlistButton,
            page,
            trailer,
            download,
            reminderButton,
        };

        const movieDetailsProps = {
            provider,
            locked,
            dolby,
            quality4K,
            onClickLock,
            rating,
            info,
            live,
        };

        const heroBlockProps = {
            picture,
            heading,
            compact,
            minHeight,
            detail,
            theme,
            blurred,
        };

        // Helper function to check if a string is a valid URL or image path
        const isValidUrl = (str: string): boolean => {
            try {
                // Try to create a URL object - works for absolute URLs (http://, https://, etc.)
                new URL(str);
                return true;
            } catch {
                // If URL constructor fails, check if it's a relative path or data URL
                // Supports: /path, ./path, ../path, data:image/...
                return /^(\.\/|\/|data:)/.test(str);
            }
        };

        const cardRef = useRef<HTMLDivElement>(null);
        const popupRef = useRef<HTMLDivElement>(null);

        const queryClient = useQueryClient();

        const [showPopup, setShowPopup] = useState(false);
        const [hovered, setHovered] = useState(false);

        const isMobile = useMediaQuery('(max-width: 924px)');

        const { start: startShowTimeout, clear: clearShowTimeout } = useTimeout(() => setShowPopup(true), 1250);
        const { start: startHideTimeout, clear: clearHideTimeout } = useTimeout(() => setShowPopup(false), 300);

        const positionPopup = useCallback(() => {
            if (!cardRef.current || !popupRef.current) {
                // console.log('Refs are not attached');
                return;
            }

            const cardRect = cardRef.current.getBoundingClientRect();
            const popupRect = popupRef.current.getBoundingClientRect();

            const popupInitialX = cardRect.left + cardRect.width / 2 - popupRect.width / 2;
            const popupInitialY = cardRect.top + cardRect.height / 2 - popupRect.height / 2;

            const adjustedLeft = Math.min(Math.max(popupInitialX, 0), window.innerWidth - popupRect.width);
            const adjustedTop = Math.min(Math.max(popupInitialY, 0), window.innerHeight - popupRect.height);
            popupRef.current.style.left = `${adjustedLeft}px`;
            popupRef.current.style.top = `${adjustedTop}px`;
        }, []);

        useEffect(() => {
            const handleScroll = () => {
                setShowPopup(false);
            };

            window.addEventListener('scroll', handleScroll);

            if (hovered) {
                clearHideTimeout();
                startShowTimeout();
            } else {
                clearShowTimeout();
                startHideTimeout();
            }

            return () => {
                window.removeEventListener('scroll', handleScroll);
                clearShowTimeout();
                clearHideTimeout();
            };
        }, [hovered, startShowTimeout, startHideTimeout, clearShowTimeout, clearHideTimeout]);

        // Position popup after it's in the DOM. Portal content is committed after this effect runs,
        // so we defer to the next tick (and optionally retry) so refs are attached.
        useEffect(() => {
            if (!(showPopup || hovered) || isMobile) return;

            const schedulePosition = (attempt = 0) => {
                const run = () => {
                    if (cardRef.current && popupRef.current) {
                        positionPopup();
                        return;
                    }
                    if (attempt < 5) {
                        setTimeout(() => schedulePosition(attempt + 1), 16 * (attempt + 1));
                    }
                };
                if (attempt === 0) {
                    requestAnimationFrame(run);
                } else {
                    run();
                }
            };

            schedulePosition();
        }, [showPopup, hovered, isMobile, positionPopup]);

        const handleMouseEnter = () => {
            setHovered(true);
            if (setIsActive) setIsActive(true);
        };

        const handleMouseLeave = () => {
            if (queryClient) {
                queryClient.invalidateQueries({ queryKey: ['streamUrl'] });
            }

            setHovered(false);
            if (setIsActive) setIsActive(false);
        };

        useImperativeHandle(ref, () => ({
            showPopup: () => setShowPopup(true),
            hidePopup: () => setShowPopup(false),
        }));

        const popupContent = (
            <div
                ref={popupRef}
                className={cn(styles.popup, {
                    [styles.fadeIn]: showPopup,
                    [styles.fadeOut]: !showPopup,
                })}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <HeroBlock {...heroBlockProps} compact>
                    {/* This content will be passed as children */}
                    {isMobile && <MovieButtons {...movieButtonsProps} className={styles.trailer} />}
                </HeroBlock>
                <div className={styles.foot}>
                    <MovieButtons {...movieButtonsProps} />
                    <MovieDetails {...movieDetailsProps} />
                </div>
            </div>
        );

        if (isLoading) {
            return (
                <div className={cn(className, styles.card)} {...restDivProps}>
                    <Skeleton absolute />
                </div>
            );
        }

        return (
            <div
                {...restDivProps}
                ref={cardRef}
                className={cn(className, styles.card, styles[layout])}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={onClick}
                id={guid}
                data-test-id={guid}
            >
                <div className={styles.picture}>
                    <LazyImage src={cover} alt={typeof heading === 'string' ? heading : 'Movie poster image'} />
                    {/* <img src={cover} alt="" /> */}
                </div>

                {!isMobile &&
                    (showPopup || hovered) &&
                    (ReactDOM.createPortal(popupContent, document.body) as ReactNode)}
                {timer && timer}
                {price && (
                    <div className={cn(styles.rent, { [styles.rented]: rented })}>
                        {typeof price === 'string' && price}
                        {typeof price === 'object' && <Price {...price} />}
                    </div>
                )}
                {icon && (
                    <div className={cn(styles.icon, `${styles[icon]}`)}>
                        <FontIcon name={icon === 'remind' ? 'bell-filled' : 'lock'} size="x-small" md="smaller" />
                    </div>
                )}
                {layout === 'vertical-2x' && (
                    <>
                        {provider?.[0] && (
                            <div className={styles.provider}>
                                <ImageContainer src={provider[0]} />
                            </div>
                        )}
                        {headingImage && isValidUrl(headingImage) && (
                            <div className={styles.name}>
                                <ImageContainer src={headingImage} />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }
);
